<template>
  <div v-if="data.detail" class="modal-backdrop" @click.self="closeDetail">
    <section class="modal message-detail-modal">
      <header>
        <div>
          <h3>记录详情</h3>
        </div>
        <button class="icon-button" title="关闭" aria-label="关闭记录详情" @click="closeDetail"><X :size="18" /></button>
      </header>

      <div class="detail-body">
        <section class="message-title-section">
          <label>
            标题
            <input v-model="displayName" maxlength="200" :placeholder="defaultDisplayName" @blur="flushDetail" />
          </label>
        </section>

        <section v-if="data.detail.attachments.length">
          <h4>附件</h4>
          <div class="attachment-grid">
            <article v-for="attachment in data.detail.attachments" :key="attachment.id" class="attachment-chip">
              <button
                class="attachment-preview-trigger attachment-grid-trigger"
                :title="`预览 ${attachment.filename}`"
                @click="selectedAttachment = attachment"
              >
                <img v-if="imageUrls[attachment.id]" :src="imageUrls[attachment.id]" alt="" />
                <Paperclip v-else :size="16" />
                <span>{{ attachment.filename }}</span>
              </button>
              <button title="下载附件" aria-label="下载附件" @click="run(() => data.downloadAttachment(attachment))"><Download :size="14" /></button>
            </article>
          </div>
        </section>

        <section v-if="derivedCount || ['pending', 'processing', 'failed'].includes(data.detail.message.ai_status)">
          <div class="section-title">
            <h4>整理结果</h4>
            <span v-if="data.detail.message.ai_status === 'pending' || data.detail.message.ai_status === 'processing'">自动整理中</span>
            <span v-else-if="data.detail.message.ai_status === 'failed'">整理失败，可重试</span>
            <span v-else-if="data.detail.message.ai_needs_confirmation">待确认</span>
            <span v-else-if="derivedCount === 0">尚未整理</span>
          </div>
          <p v-if="data.detail.message.ai_summary" class="empty">{{ data.detail.message.ai_summary }}</p>
          <button v-if="data.detail.message.ai_status === 'failed'" @click="run(data.retryAi)">重新自动整理</button>
          <div v-if="derivedCount" class="derived-list">
            <article v-for="todo in data.detail.todos" :key="todo.id" class="derived-item">
              <CircleCheck :size="17" />
              <div><strong>{{ todo.title }}</strong><span>{{ todoPriorityLabel(todo.priority) }}{{ todo.due_at ? ` · ${formatDate(todo.due_at)}` : "" }}</span></div>
            </article>
            <article v-for="reminder in data.detail.reminders" :key="reminder.id" class="derived-item">
              <Bell :size="17" />
              <div><strong>{{ reminder.title }}</strong><span>{{ formatDate(reminder.remind_at) }}</span></div>
            </article>
            <article v-for="schedule in data.detail.schedules" :key="schedule.id" class="derived-item">
              <CalendarPlus :size="17" />
              <div><strong>{{ schedule.title }}</strong><span>{{ formatDate(schedule.start_at) }}</span></div>
            </article>
            <article v-for="project in data.detail.projects" :key="project.id" class="derived-item">
              <FolderOpen :size="17" />
              <div><strong>{{ project.name }}</strong><span>项目关联</span></div>
              <button title="移除项目关联" aria-label="移除项目关联" @click.stop="run(() => data.removeProjectMessage(project.id))"><Unlink :size="14" /></button>
            </article>
          </div>
        </section>

        <section>
          <label>
            整理备注
            <textarea v-model="organizingNote" rows="3" placeholder="补充只有你自己需要记住的上下文" @blur="flushDetail" />
          </label>
          <p class="auto-save-state" :class="`is-${saveState}`" role="status" aria-live="polite">{{ saveStateLabel }}</p>
        </section>

        <section class="organize-section">
          <h4>处理这条记录</h4>
          <div class="action-picker" role="group" aria-label="选择记录处理动作">
            <button :class="{ active: selectedAction === 'todo' }" @click="selectedAction = 'todo'"><CircleCheck :size="17" />转待办</button>
            <button :class="{ active: selectedAction === 'reminder' }" @click="selectedAction = 'reminder'"><Bell :size="17" />转提醒</button>
            <button :class="{ active: selectedAction === 'schedule' }" @click="selectedAction = 'schedule'"><CalendarPlus :size="17" />转日程</button>
            <button :class="{ active: selectedAction === 'project' }" @click="selectedAction = 'project'"><FolderOpen :size="17" />加入项目</button>
            <button @click="run(() => data.detailAction('archive'))"><Archive :size="17" />归档记录</button>
          </div>

          <div v-if="selectedAction" class="progressive-form">
            <template v-if="selectedAction === 'todo'">
              <label>
                截止时间（可选）
                <input v-model="todoDueAt" type="datetime-local" />
              </label>
              <label>
                优先级
                <AppSelect v-model="todoPriority" :options="priorityOptions" aria-label="待办优先级" />
              </label>
            </template>
            <label v-if="selectedAction === 'reminder'">
              提醒时间
              <input v-model="reminderAt" type="datetime-local" />
            </label>
            <template v-if="selectedAction === 'schedule'">
              <label>
                开始时间
                <input v-model="scheduleStartAt" type="datetime-local" />
              </label>
              <label>
                结束时间（可选）
                <input v-model="scheduleEndAt" type="datetime-local" />
              </label>
              <label v-if="isAndroid" class="inline-check">
                <input v-model="scheduleAlarmEnabled" type="checkbox" />
                同时设置 Android 闹钟
              </label>
            </template>
            <label v-if="selectedAction === 'project'">
              项目
              <AppSelect v-model="projectId" :options="projectOptions" placeholder="选择项目" aria-label="选择项目" />
            </label>
            <button class="primary" :disabled="actionDisabled" @click="applyAction">
              <Check :size="17" />确认{{ actionLabel }}
            </button>
          </div>
        </section>

        <section class="original-record-section">
          <div class="detail-heading-row">
            <h4>原始记录</h4>
            <div class="detail-meta">
              <span>{{ formatDate(data.detail.message.created_at) }}</span>
              <span># {{ data.detail.message.channel_name || "原点" }}</span>
            </div>
          </div>
          <label>
            消息原文
            <textarea v-model="messageContent" rows="4" placeholder="这条消息没有文字原文" @blur="flushDetail" />
          </label>
        </section>

        <section class="danger-zone detail-danger-zone">
          <h4>删除原文</h4>
          <p>会同时删除 {{ data.detail.todos.length }} 个待办、{{ data.detail.reminders.length }} 个提醒、{{ data.detail.schedules.length }} 个日程，并移除 {{ data.detail.projects.length }} 个项目关联。</p>
          <button class="danger-button" @click="confirmDelete"><Trash2 :size="17" />删除原文及关联结果</button>
        </section>
      </div>
    </section>
  </div>
  <AttachmentPreviewModal :attachment="selectedAttachment" @close="selectedAttachment = null" />
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { Capacitor } from "@capacitor/core";
import { Archive, Bell, CalendarPlus, Check, CircleCheck, Download, FolderOpen, Paperclip, Trash2, Unlink, X } from "@lucide/vue";
import { attachmentThumbnailBlob } from "../attachmentImageCache";
import AttachmentPreviewModal from "./AttachmentPreviewModal.vue";
import AppSelect from "./AppSelect.vue";
import { formatDate, messageDisplayText, toLocalInput, todoPriorityLabel } from "../composables/format";
import { useDataStore } from "../stores/data";
import type { Attachment, Todo } from "../types";

const data = useDataStore();
const selectedAction = ref<"todo" | "reminder" | "schedule" | "project" | "">("");
const displayName = ref("");
const messageContent = ref("");
const organizingNote = ref("");
const saveState = ref<"saved" | "queued" | "saving" | "error">("saved");
const todoDueAt = ref("");
const todoPriority = ref<Todo["priority"]>("normal");
const reminderAt = ref("");
const scheduleStartAt = ref("");
const scheduleEndAt = ref("");
const scheduleAlarmEnabled = ref(false);
const projectId = ref("");
const imageUrls = ref<Record<string, string>>({});
const selectedAttachment = ref<Attachment | null>(null);
let imageLoadVersion = 0;
let saveTimer: ReturnType<typeof setTimeout> | undefined;
let lastSavedSnapshot = "";
let activeSave: Promise<boolean> | null = null;
const isAndroid = Capacitor.getPlatform() === "android";
const defaultDisplayName = computed(() => data.detail
  ? messageDisplayText({ ...data.detail.message, display_name: "" }, data.detail.attachments)
  : "");
const saveStateLabel = computed(() => ({
  saved: "已保存",
  queued: "等待自动保存",
  saving: "保存中…",
  error: "自动保存失败，请重试"
})[saveState.value]);
const derivedCount = computed(() => data.detail
  ? data.detail.todos.length + data.detail.reminders.length + data.detail.schedules.length + data.detail.projects.length
  : 0);
const actionLabel = computed(() => ({ todo: "转为待办", reminder: "设置提醒", schedule: "创建日程", project: "加入项目", "": "" })[selectedAction.value]);
const actionDisabled = computed(() =>
  (selectedAction.value === "reminder" && !reminderAt.value)
  || (selectedAction.value === "schedule" && !scheduleStartAt.value)
  || (selectedAction.value === "project" && !projectId.value));
const priorityOptions = [
  { value: "normal", label: "普通" },
  { value: "high", label: "高" },
  { value: "low", label: "低" }
];
const projectOptions = computed(() => [
  { value: "", label: "选择项目" },
  ...data.projects.map((project) => ({ value: project.id, label: project.name }))
]);

onMounted(() => window.addEventListener("keydown", onKeydown));
onUnmounted(() => {
  window.removeEventListener("keydown", onKeydown);
  clearTimeout(saveTimer);
  clearImageUrls();
});
watch(
  () => (data.detail ? `${data.detail.message.id}:${data.detail.attachments.map((item) => item.id).join(",")}` : ""),
  () => {
    selectedAction.value = "";
    selectedAttachment.value = null;
    displayName.value = data.detail?.message.display_name || "";
    messageContent.value = data.detail?.message.content || "";
    organizingNote.value = data.detail?.message.organizing_note || "";
    lastSavedSnapshot = detailSnapshot();
    saveState.value = "saved";
    todoDueAt.value = "";
    todoPriority.value = "normal";
    reminderAt.value = "";
    scheduleStartAt.value = toLocalInput(data.detail?.message.ai_reminder_at);
    scheduleEndAt.value = "";
    scheduleAlarmEnabled.value = false;
    projectId.value = "";
    loadImageUrls();
  },
  { immediate: true }
);

watch([displayName, messageContent, organizingNote], () => {
  if (!data.detail || detailSnapshot() === lastSavedSnapshot) return;
  saveState.value = "queued";
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => void persistDetail(), 600);
});

function onKeydown(event: KeyboardEvent) {
  if (event.key === "Escape" && data.detail) void closeDetail();
}

function detailSnapshot() {
  return JSON.stringify({ display_name: displayName.value.trim(), content: messageContent.value, organizing_note: organizingNote.value });
}

async function persistDetail() {
  clearTimeout(saveTimer);
  if (!data.detail || detailSnapshot() === lastSavedSnapshot) {
    saveState.value = "saved";
    return true;
  }
  if (activeSave) {
    await activeSave;
    return persistDetail();
  }
  const snapshot = detailSnapshot();
  const payload = JSON.parse(snapshot) as { display_name: string; content: string; organizing_note: string };
  saveState.value = "saving";
  activeSave = data.saveDetail(payload).then(() => {
    lastSavedSnapshot = snapshot;
    if (detailSnapshot() === snapshot) {
      saveState.value = "saved";
    } else {
      saveState.value = "queued";
      saveTimer = setTimeout(() => void persistDetail(), 600);
    }
    return true;
  }).catch(() => {
    saveState.value = "error";
    return false;
  }).finally(() => {
    activeSave = null;
  });
  return activeSave;
}

async function flushDetail() {
  return persistDetail();
}

async function closeDetail() {
  if (await flushDetail()) data.closeMessage();
}

async function run(action: () => Promise<unknown> | void) {
  await action();
}

async function applyAction() {
  if (selectedAction.value === "project") {
    await data.attachDetailProject(projectId.value);
  } else if (selectedAction.value) {
    await data.detailAction(selectedAction.value, {
      todoDueAt: todoDueAt.value,
      todoPriority: todoPriority.value,
      reminderAt: reminderAt.value,
      scheduleStartAt: scheduleStartAt.value,
      scheduleEndAt: scheduleEndAt.value,
      scheduleAlarmEnabled: scheduleAlarmEnabled.value
    });
  }
  selectedAction.value = "";
}

async function confirmDelete() {
  if (!data.detail) return;
  const impact = `删除原文将同时删除 ${data.detail.todos.length} 个待办、${data.detail.reminders.length} 个提醒、${data.detail.schedules.length} 个日程，并移除 ${data.detail.projects.length} 个项目关联。继续？`;
  if (!window.confirm(impact)) return;
  await data.deleteCurrentMessage();
}

async function loadImageUrls() {
  clearImageUrls();
  imageLoadVersion += 1;
  const currentVersion = imageLoadVersion;
  const attachments = data.detail?.attachments.filter((item) => item.mime_type.startsWith("image/")) || [];
  const loaded = await Promise.all(attachments.map(async (attachment) => ({
    id: attachment.id,
    url: await attachmentThumbnailBlob(attachment.id).then((blob) => URL.createObjectURL(blob)).catch(() => "")
  })));
  if (currentVersion !== imageLoadVersion) {
    loaded.forEach((item) => item.url && URL.revokeObjectURL(item.url));
    return;
  }
  imageUrls.value = Object.fromEntries(loaded.filter((item) => item.url).map((item) => [item.id, item.url]));
}

function clearImageUrls() {
  imageLoadVersion += 1;
  Object.values(imageUrls.value).forEach((url) => URL.revokeObjectURL(url));
  imageUrls.value = {};
}
</script>
